import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const BookingCard = ({ booking }) => {
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'confirmed':
        return 'text-success bg-success/10';
      case 'pending':
        return 'text-warning bg-warning/10';
      case 'cancelled':
        return 'text-destructive bg-destructive/10';
      default:
        return 'text-muted-foreground bg-muted';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-GB', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatTime = (timeString) => {
    const time = new Date(`2000-01-01T${timeString}`);
    return time?.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  };

  const isUpcoming = () => {
    const eventDate = new Date(booking.date);
    const today = new Date();
    return eventDate >= today;
  };

  const handleManageBooking = () => {
    window.open(booking?.ticketUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="bg-background border border-border rounded-lg overflow-hidden shadow-organic">
      <div className="flex flex-col sm:flex-row">
        {/* Image */}
        <div className="w-full sm:w-32 h-32 sm:h-auto overflow-hidden flex-shrink-0">
          <Image
            src={booking?.image}
            alt={booking?.eventName}
            className="w-full h-full object-cover"
          />
        </div>

        {/* Content */}
        <div className="flex-1 p-4">
          <div className="flex items-start justify-between mb-2">
            <div className="flex-1">
              <h3 className="font-semibold text-foreground text-lg leading-tight mb-1">
                {booking?.eventName}
              </h3>
              <p className="text-sm text-muted-foreground mb-2">
                by {booking?.businessName}
              </p>
            </div>
            <div className="ml-4 flex-shrink-0">
              <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(booking?.status)}`}>
                {booking?.status}
              </span>
            </div>
          </div>

          {/* Date and Time */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-3">
            <div className="flex items-center text-sm text-foreground">
              <Icon name="Calendar" size={16} className="mr-2" />
              {formatDate(booking?.date)}
            </div>
            <div className="flex items-center text-sm text-foreground">
              <Icon name="Clock" size={16} className="mr-2" />
              {formatTime(booking?.time)}
            </div>
          </div>

          {/* Location */}
          <div className="flex items-center text-sm text-muted-foreground mb-3">
            <Icon name="MapPin" size={16} className="mr-2" />
            {booking?.location}
          </div>

          {/* Booking Details */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-4 text-sm">
              <span className="text-muted-foreground">
                Booking ID: <span className="font-mono text-foreground">{booking?.bookingId}</span>
              </span>
              <span className="text-muted-foreground">
                Tickets: <span className="font-medium text-foreground">{booking?.ticketCount}</span>
              </span>
            </div>

            <div className="flex items-center gap-2">
              {isUpcoming() && booking?.status?.toLowerCase() === 'confirmed' && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleManageBooking}
                  iconName="ExternalLink"
                  iconPosition="right"
                  iconSize={14}
                >
                  Manage Booking
                </Button>
              )}
              
              {booking?.status?.toLowerCase() === 'pending' && (
                <Button
                  variant="default"
                  size="sm"
                  onClick={handleManageBooking}
                  iconName="CreditCard"
                  iconPosition="left"
                  iconSize={14}
                >
                  Complete Payment
                </Button>
              )}
            </div>
          </div>

          {/* Additional Info */}
          {booking?.notes && (
            <div className="mt-3 p-3 bg-muted rounded-lg">
              <p className="text-sm text-foreground">
                <Icon name="Info" size={14} className="inline mr-1" />
                {booking?.notes}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BookingCard;